import { useCallback, useEffect, useMemo, useState } from "react";
import { TradingViewRecentSignal, api, isError } from "../api";
import { buildAlertTemplate } from "../tradingviewTemplate";

type SelfTestAction = "buy" | "sell" | "close" | "veto";

export function TradingViewSetupPage() {
  const [recent, setRecent] = useState<TradingViewRecentSignal[] | null>(null);
  const [secretConfigured, setSecretConfigured] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [secret, setSecret] = useState<string | null>(null);
  const [secretRevealing, setSecretRevealing] = useState(false);
  const [secretRotating, setSecretRotating] = useState(false);

  // Message-template builder inputs.
  const [strategyId, setStrategyId] = useState("my-strategy-v1");
  const [symbol, setSymbol] = useState("EURUSD");

  // Self-test inputs (separate from template so each can be tweaked
  // independently).
  const [testSymbol, setTestSymbol] = useState("EURUSD");
  const [testAction, setTestAction] = useState<SelfTestAction>("buy");
  const [testPrice, setTestPrice] = useState("1.0850");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{
    // "warn" = accepted but stopped short of execution (strict convergence
    // mode), which must not read as a clean pass.
    tone: "ok" | "warn" | "error";
    text: string;
    detail?: string;
  } | null>(null);

  const webhookUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/webhook/tradingview`;
  }, []);

  const refresh = useCallback(async () => {
    const r = await api.tradingviewSetup();
    if (isError(r)) {
      setLoadError(r.error);
      return;
    }
    setLoadError(null);
    setRecent(r.recent_signals);
    setSecretConfigured(r.secret_configured);
  }, []);

  useEffect(() => {
    void refresh();
    // Light auto-refresh of the recent-signals table so the operator
    // sees a TradingView alert land within a couple of seconds.
    const id = window.setInterval(() => void refresh(), 5_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  async function reveal() {
    setSecretRevealing(true);
    const r = await api.tradingviewSecret();
    setSecretRevealing(false);
    if (isError(r)) {
      setTestResult({
        tone: "error",
        text: `Couldn't read secret: ${r.error}`,
      });
      return;
    }
    setSecret(r.value);
  }

  async function rotate() {
    if (
      !window.confirm(
        "Generate a new webhook secret? Every existing TradingView alert will be " +
          "rejected until you paste the new secret into its Message body.",
      )
    ) {
      return;
    }
    setSecretRotating(true);
    const r = await api.tradingviewRotateSecret();
    setSecretRotating(false);
    if (isError(r)) {
      setTestResult({ tone: "error", text: `Couldn't rotate secret: ${r.error}` });
      return;
    }
    setSecret(r.value);
    setSecretConfigured(true);
    setTestResult({
      tone: "ok",
      text: "New secret generated. Update the Message body in each TradingView alert.",
    });
  }

  async function runSelfTest() {
    const priceNum = Number(testPrice);
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setTestResult({ tone: "error", text: "Price must be a positive number." });
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    const r = await api.tradingviewSelfTest(testSymbol, testAction, priceNum);
    setTestBusy(false);
    if (isError(r)) {
      setTestResult({ tone: "error", text: r.error });
      return;
    }
    if (r.ok) {
      // A warning means the signal was accepted but stopped short of
      // execution — in strict mode that is the normal outcome. Showing it as
      // a plain success would put a green tick over an untested path.
      setTestResult({
        tone: r.warning ? "warn" : "ok",
        text: r.warning
          ? `Signal accepted (${r.status_code}), but the chain stopped at ${r.reached ?? "an earlier stage"}. ${r.warning}`
          : `Signal accepted (${r.status_code}) and reached the executor. Check the recent-signals list below — your test should appear within a second.`,
        detail: prettyJson(r.response),
      });
      // Bump the recent-signals refresh so the new row shows up immediately.
      void refresh();
    } else {
      setTestResult({
        tone: "error",
        text: r.error
          ? `Self-test failed: ${r.error}`
          : `Self-test failed with HTTP ${r.status_code ?? "?"}.`,
        detail: prettyJson(r.response ?? r),
      });
    }
  }

  const templateJson = useMemo(
    () => buildAlertTemplate({ secret, strategyId, symbol }),
    [secret, strategyId, symbol],
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">TradingView</h1>
          <p className="text-xs text-fg-muted">
            connect TradingView alerts to FX-Dash — copy the URL + secret,
            paste into your alert, send a self-test to verify
          </p>
        </div>
      </header>

      {loadError && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {loadError}
        </p>
      )}
      {!secretConfigured && (
        <p className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
          <strong>No webhook secret is configured</strong> (or the .env still
          has the old placeholder). TradingView alerts can&apos;t authenticate
          without one — click <em>generate secret</em> below.
        </p>
      )}

      {/* Webhook URL */}
      <section className="rounded-xl border border-border bg-surface p-5">
        <h2 className="mb-2 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
          1. Webhook URL
        </h2>
        <p className="mb-3 text-xs text-fg-muted">
          Paste this into TradingView&apos;s alert <strong>Webhook URL</strong> field.
        </p>
        <CopyRow value={webhookUrl} />
      </section>

      {/* Secret */}
      <section className="rounded-xl border border-border bg-surface p-5">
        <h2 className="mb-2 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
          2. Secret
        </h2>
        <p className="mb-3 text-xs text-fg-muted">
          Goes inside the JSON <code className="font-mono">secret</code> field
          in the Message body below. Anyone with this secret can submit
          signals — keep it private.
        </p>
        <div className="flex items-center gap-2">
          {secret == null ? (
            <button
              type="button"
              onClick={() => void reveal()}
              disabled={secretRevealing || !secretConfigured}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {secretRevealing ? "revealing…" : "reveal secret"}
            </button>
          ) : (
            <div className="min-w-0 flex-1">
              <CopyRow value={secret} mono />
            </div>
          )}
          <button
            type="button"
            onClick={() => void rotate()}
            disabled={secretRotating}
            className="rounded-md border border-border px-2 py-1.5 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-danger/10 hover:text-danger disabled:opacity-50"
          >
            {secretRotating ? "…" : secretConfigured ? "rotate secret" : "generate secret"}
          </button>
        </div>
      </section>

      {/* Message template */}
      <section className="rounded-xl border border-border bg-surface p-5">
        <h2 className="mb-2 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
          3. Message body
        </h2>
        <p className="mb-3 text-xs text-fg-muted">
          Paste into TradingView&apos;s alert <strong>Message</strong> field.
          The <code className="font-mono">{`{{strategy.order.action}}`}</code>,{" "}
          <code className="font-mono">{`{{close}}`}</code> and{" "}
          <code className="font-mono">{`{{timenow}}`}</code> placeholders
          are TradingView&apos;s — they expand at alert-fire time.
        </p>
        <p className="mb-3 text-xs text-fg-muted">
          <code className="font-mono">{`{{strategy.order.action}}`}</code> only
          expands in a Pine <code className="font-mono">strategy()</code>{" "}
          script. For an <code className="font-mono">indicator()</code> it
          expands to nothing and the signal is rejected — use one alert per
          direction with <code className="font-mono">&quot;action&quot;</code>{" "}
          hardcoded, or build the whole body in Pine with{" "}
          <code className="font-mono">alert()</code>.
        </p>
        <div className="mb-3 grid grid-cols-[max-content_1fr] items-center gap-x-3 gap-y-2 text-sm">
          <label htmlFor="tv-strategy" className="text-fg-muted">
            Strategy ID
          </label>
          <input
            id="tv-strategy"
            type="text"
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value)}
            className="w-72 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
          <label htmlFor="tv-symbol" className="text-fg-muted">
            Symbol
          </label>
          <input
            id="tv-symbol"
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            className="w-72 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <pre className="overflow-x-auto rounded-md border border-border bg-page px-3 py-2 font-mono text-xs text-fg">
{templateJson}
        </pre>
        <div className="mt-2 flex items-center gap-2">
          <CopyButton value={templateJson} />
          <span className="text-xs text-fg-muted">
            Symbol must match MT5 exactly — check your MT5 Market Watch
            (Exness uses <code className="font-mono">EURUSD</code> or{" "}
            <code className="font-mono">EURUSDm</code> for cent accounts).
          </span>
        </div>
      </section>

      {/* Self-test */}
      <section className="rounded-xl border border-border bg-surface p-5">
        <h2 className="mb-2 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
          4. Send a self-test
        </h2>
        <p className="mb-3 text-xs text-fg-muted">
          Fires a synthetic signal through the real{" "}
          <code className="font-mono">/webhook/tradingview</code> path —
          exercises the secret check, signal persistence, convergence,
          and (if bridge connected) executor. The same path your real
          TradingView alerts will take.
        </p>
        <div className="grid grid-cols-[max-content_1fr] items-center gap-x-3 gap-y-2 text-sm">
          <label htmlFor="test-symbol" className="text-fg-muted">
            Symbol
          </label>
          <input
            id="test-symbol"
            type="text"
            value={testSymbol}
            onChange={(e) => setTestSymbol(e.target.value.toUpperCase())}
            className="w-48 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            disabled={testBusy}
          />
          <label htmlFor="test-action" className="text-fg-muted">
            Action
          </label>
          <select
            id="test-action"
            value={testAction}
            onChange={(e) => setTestAction(e.target.value as SelfTestAction)}
            className="w-32 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            disabled={testBusy}
          >
            <option value="buy">buy</option>
            <option value="sell">sell</option>
            <option value="close">close</option>
            <option value="veto">veto</option>
          </select>
          <label htmlFor="test-price" className="text-fg-muted">
            Price
          </label>
          <input
            id="test-price"
            type="number"
            step="any"
            value={testPrice}
            onChange={(e) => setTestPrice(e.target.value)}
            className="w-32 rounded-md border border-border bg-page px-2 py-1 text-right font-mono text-sm tabular-nums text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            disabled={testBusy}
          />
        </div>
        <div className="mt-3">
          <button
            type="button"
            onClick={() => void runSelfTest()}
            disabled={testBusy || !secretConfigured}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {testBusy ? "sending…" : "send test signal"}
          </button>
        </div>
        {testResult && (
          <div
            className={`mt-3 rounded-md border px-3 py-2 text-xs ${
              testResult.tone === "ok"
                ? "border-success/40 bg-success/10 text-success"
                : testResult.tone === "warn"
                  ? "border-warn/40 bg-warn/10 text-warn"
                  : "border-danger/40 bg-danger/10 text-danger"
            }`}
          >
            <p>{testResult.text}</p>
            {testResult.detail && (
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-fg-muted">
                {testResult.detail}
              </pre>
            )}
          </div>
        )}
      </section>

      {/* Recent inbound signals */}
      <section className="rounded-xl border border-border bg-surface p-5">
        <h2 className="mb-2 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
          5. Recent inbound signals
        </h2>
        <p className="mb-3 text-xs text-fg-muted">
          The last 10 signals to hit <code className="font-mono">/webhook/tradingview</code> —
          refreshes every 5 seconds. After saving your TradingView alert,
          trigger the alert (or wait for the condition) and watch a row
          land here.
        </p>
        {recent === null ? (
          <p className="text-sm text-fg-muted">Loading…</p>
        ) : recent.length === 0 ? (
          <p className="text-sm text-fg-muted">
            No signals received yet. Send a self-test above to verify the
            path works.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-[10px] uppercase tracking-wider text-fg-muted">
                <tr>
                  <th className="px-2 py-2 font-medium">When</th>
                  <th className="px-2 py-2 font-medium">Strategy</th>
                  <th className="px-2 py-2 font-medium">Symbol</th>
                  <th className="px-2 py-2 font-medium">Action</th>
                  <th className="px-2 py-2 text-right font-medium">Price</th>
                  <th className="px-2 py-2 font-medium">Source IP</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border/60 last:border-b-0 hover:bg-bg-hover/40"
                  >
                    <td className="px-2 py-1.5 font-mono text-xs text-fg-muted">
                      {row.received_at
                        ? new Date(row.received_at).toLocaleTimeString()
                        : "—"}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">
                      {row.strategy_id}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs font-semibold">
                      {row.symbol}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                          row.action === "buy"
                            ? "bg-success/15 text-success"
                            : row.action === "sell"
                              ? "bg-danger/15 text-danger"
                              : "bg-fg-muted/15 text-fg-muted"
                        }`}
                      >
                        {row.action}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs tabular-nums">
                      {row.price ?? "—"}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[11px] text-fg-muted">
                      {row.source_ip ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---- helpers ----------------------------------------------------------

function CopyRow({ value, mono = true }: { value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className={`min-w-0 flex-1 rounded-md border border-border bg-page px-2 py-1 ${
          mono ? "font-mono" : ""
        } text-sm text-fg outline-none`}
      />
      <CopyButton value={value} />
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts; the user can
      // still select the field manually.
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
    >
      {copied ? "copied" : "copy"}
    </button>
  );
}

function prettyJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
